import { useEffect, useState, type FormEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { FormErrorAlert } from '@/features/auth/FormErrorAlert';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import {
  EMPTY_FORM_ERRORS,
  fieldError,
  formErrorsFromApiError,
  hasFormErrors,
  type FormErrors,
} from '@/lib/form-errors';
import { leadStatus, leadStatusOrder } from '@/theme/status';
import type { PurgaLeadsResultado } from '@/types/crm';
import type { EstadoLead } from '@/types/domain';
import {
  PURGA_DIAS_DEFAULT,
  PURGA_DIAS_MAX,
  PURGA_LIMITE_DEFAULT,
  PURGA_LIMITE_MAX,
  countLabel,
  describePurga,
  describePurgaEjemplo,
  formatNumber,
} from './crm-format';
import {
  PURGA_FORM_FIELDS,
  emptyPurgaForm,
  purgaLeadsPayload,
  type PurgaFormState,
} from './crm-payload';
import { validatePurgaForm } from './crm-validation';
import { usePurgeLeads } from './use-crm';

/**
 * Purga de conversaciones (`POST .../crm/purgar`, solo ADMIN).
 *
 * Es el borrado por lotes de los chats que no van a volver a usarse: gente que preguntó algo y no
 * compró, a la que nadie va a contestar ya. Se usa por DOS razones —dejar el Inbox y el CRM sin ruido,
 * y poder atender un pedido de supresión de datos—, NO para liberar disco: los mensajes de texto
 * ocupan muy poco y lo que llena un servidor son las fotos del catálogo y los respaldos.
 *
 * El diálogo obliga a pasar por una SIMULACIÓN antes de borrar: cuenta cuántas caen, cuántas quedan
 * protegidas por tener órdenes y muestra hasta 10 ejemplos. Solo con ese número delante aparece el
 * botón rojo, y lo que se borra es exactamente lo que se contó (mismo body, `confirmar: true`).
 *
 * El botón que abre este diálogo solo aparece con rol ADMIN (el backend responde 403 igual).
 */

export interface PurgeDialogProps {
  open: boolean;
  onClose: () => void;
}

export function PurgeDialog({ open, onClose }: PurgeDialogProps) {
  const { tenantId } = useActiveTenantAccess();
  const purga = usePurgeLeads(tenantId);

  const [form, setForm] = useState<PurgaFormState>(emptyPurgaForm);
  const [errors, setErrors] = useState<FormErrors>(EMPTY_FORM_ERRORS);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<PurgaLeadsResultado | null>(null);

  /** Cada apertura arranca limpia: sin el conteo de la vez anterior. */
  useEffect(() => {
    if (!open) return;

    setForm(emptyPurgaForm());
    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
    setResultado(null);
  }, [open]);

  /** Valida y arma el body; `null` si el formulario todavía no puede viajar. */
  function prepararPayload(confirmar: boolean) {
    const nextErrors = validatePurgaForm(form.diasSinActividad, form.limite);

    if (hasFormErrors(nextErrors)) {
      setErrors(nextErrors);
      setSubmitError(null);
      return null;
    }

    setErrors(EMPTY_FORM_ERRORS);
    setSubmitError(null);
    return purgaLeadsPayload(form, confirmar);
  }

  /** Simulación (`false`) y borrado real (`true`) comparten validación y body. */
  async function correr(confirmar: boolean) {
    const payload = prepararPayload(confirmar);
    if (!payload) return;

    try {
      setResultado(await purga.mutateAsync(payload));
    } catch (error) {
      setErrors(formErrorsFromApiError(error, PURGA_FORM_FIELDS));
      setSubmitError(getErrorMessage(error));
    }
  }

  function handleSimular(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void correr(false);
  }

  const puedeBorrar = Boolean(resultado && !resultado.confirmado && resultado.seleccionadas > 0);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Borrar conversaciones viejas"
      description="Elimina los chats que no se van a volver a usar y deja el CRM sin ruido."
      size="lg"
      footer={
        resultado?.confirmado ? (
          <Button onClick={onClose}>Cerrar</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={purga.isPending}>
              Cancelar
            </Button>

            <Button type="submit" form="crm-purga-form" variant="secondary" loading={purga.isPending}>
              {resultado ? 'Volver a contar' : 'Contar qué se borraría'}
            </Button>

            {puedeBorrar ? (
              <Button variant="danger" loading={purga.isPending} onClick={() => void correr(true)}>
                {`Borrar ${countLabel(
                  resultado?.seleccionadas ?? 0,
                  'conversación',
                  'conversaciones',
                )}`}
              </Button>
            ) : null}
          </>
        )
      }
    >
      {resultado ? (
        <div className="space-y-4">
          <Alert tone={resultado.confirmado ? 'success' : 'warning'}>
            {describePurga(resultado)}
          </Alert>

          <p className="text-caption text-ink-muted">
            Las conversaciones con órdenes NO se borran nunca: una venta no puede quedarse sin el chat
            que la originó, así que se cuentan aparte. Y si un cliente vuelve a escribir, entra como
            conversación nueva: el bot no recuerda lo que se habló antes.
          </p>

          {resultado.ejemplos.length === 0 ? (
            <p className="text-caption text-ink-muted">
              No hay ejemplos que mostrar: ninguna conversación cumple el filtro.
            </p>
          ) : (
            <div className="space-y-2">
              <h3 className="text-caption font-semibold text-ink">
                {`Las ${formatNumber(resultado.ejemplos.length)} más viejas`}
              </h3>

              <ul className="scroll-thin max-h-56 space-y-1.5 overflow-y-auto">
                {resultado.ejemplos.map((ejemplo) => (
                  <li
                    key={ejemplo.conversationId}
                    className="rounded-panel border border-line px-3 py-2 text-caption text-ink-soft"
                  >
                    {describePurgaEjemplo(ejemplo)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {resultado.confirmado ? null : (
            <p className="text-micro text-ink-faint">
              Nada se ha borrado todavía: esto fue la simulación.
            </p>
          )}
        </div>
      ) : (
        <form id="crm-purga-form" onSubmit={handleSimular} noValidate className="space-y-4">
          <FormErrorAlert message={submitError} errors={errors} />

          <Field
            label="Estado del lead"
            htmlFor="crmPurgaEstado"
            hint="Solo borra las conversaciones que estén en ese estado. En «Todos» no filtra."
          >
            <Select
              id="crmPurgaEstado"
              name="estadoLead"
              value={form.estadoLead}
              onChange={(event) =>
                setForm({ ...form, estadoLead: event.target.value as EstadoLead | '' })
              }
            >
              <option value="">Todos los estados</option>
              {leadStatusOrder.map((item) => (
                <option key={item} value={item}>
                  {leadStatus[item].label}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            label="Días sin actividad"
            htmlFor="crmPurgaDias"
            required
            hint={`Entre 1 y ${formatNumber(PURGA_DIAS_MAX)}. Por defecto ${String(
              PURGA_DIAS_DEFAULT,
            )}: tres meses sin escribir es «no va a volver».`}
            error={fieldError(errors, 'diasSinActividad')}
          >
            <Input
              id="crmPurgaDias"
              name="diasSinActividad"
              type="number"
              min={1}
              max={PURGA_DIAS_MAX}
              step={1}
              inputMode="numeric"
              value={form.diasSinActividad}
              invalid={Boolean(fieldError(errors, 'diasSinActividad'))}
              onChange={(event) => setForm({ ...form, diasSinActividad: event.target.value })}
            />
          </Field>

          <Field
            label="Conversaciones por corrida"
            htmlFor="crmPurgaLimite"
            required
            hint={`Entre 1 y ${formatNumber(PURGA_LIMITE_MAX)}. Por defecto ${String(
              PURGA_LIMITE_DEFAULT,
            )}: si quedan más candidatas, el conteo lo dice y puedes repetir.`}
            error={fieldError(errors, 'limite')}
          >
            <Input
              id="crmPurgaLimite"
              name="limite"
              type="number"
              min={1}
              max={PURGA_LIMITE_MAX}
              step={1}
              inputMode="numeric"
              value={form.limite}
              invalid={Boolean(fieldError(errors, 'limite'))}
              onChange={(event) => setForm({ ...form, limite: event.target.value })}
            />
          </Field>

          <Alert tone="warning">
            Esto borra los mensajes y el historial del lead para siempre (no hay papelera). El nombre
            del cliente vive en sus órdenes, así que una conversación con pedidos no se puede borrar
            desde acá.
          </Alert>
        </form>
      )}
    </Modal>
  );
}
